// ChapterLLMSelector.tsx - LLM Model Selector for Chapter Generation
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient';
import { ChevronDown } from 'lucide-react';

interface LlmModel {
  id: number;
  provider: string;
  modelName: string;
  apiName: string;
  creditMultiplier: number;
  minPlanRequired: string;
  isActive: boolean;
  contextWindow?: string;
}

interface ChapterLLMSelectorProps {
  taskType: 'chapter-preview' | 'chapter-full-text';
  onModelSelect: (provider: string, model: string) => void;
  selectedModel?: { provider: string; model: string };
  disabled?: boolean;
}

const TASK_DISPLAY_NAMES = {
  'chapter-preview': 'Preview Generation',
  'chapter-full-text': 'Full Text Generation'
};

const ChapterLLMSelector: React.FC<ChapterLLMSelectorProps> = ({
  taskType,
  onModelSelect,
  selectedModel,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Fetch available models
  const { data: models = [], isLoading } = useQuery<LlmModel[]>({
    queryKey: ['/api/llm-models'],
    queryFn: async () => {
      const response = await apiRequest('/api/llm-models', 'GET');
      const allModels = await response.json();
      return (allModels as LlmModel[]).filter((model: LlmModel) => model.isActive);
    }
  });

  // Group models by provider
  const modelsByProvider = models.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<string, LlmModel[]>);

  // Get current selection display
  const getCurrentDisplay = () => {
    if (selectedModel) {
      const model = models.find(m => 
        m.provider === selectedModel.provider && 
        m.apiName === selectedModel.model
      );
      if (model) {
        return `${model.provider.toUpperCase()}: ${model.modelName} (${model.creditMultiplier}x credits)`;
      }
    }
    return 'Gemini 2.5 Flash (2x credits) - Default';
  };

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-gray-700 mb-1">
        Model for {TASK_DISPLAY_NAMES[taskType]}
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
          className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-between">
            <span className="truncate">{getCurrentDisplay()}</span>
            <ChevronDown 
              className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {isOpen && !disabled && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            <div className="py-1">
              {/* Default option */}
              <button
                onClick={() => {
                  onModelSelect('', ''); // Empty means use default
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Default (Gemini 2.5 Flash)</div>
                    <div className="text-xs text-gray-500">Recommended for most tasks</div>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">2x credits</span>
                </div>
              </button>

              {Object.entries(modelsByProvider).map(([provider, providerModels]) => (
                <div key={provider}>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-600 bg-gray-50 border-t">
                    {provider.toUpperCase()}
                  </div>
                  {Array.isArray(providerModels) && providerModels.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        onModelSelect(model.provider, model.apiName);
                        setIsOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{model.modelName}</div>
                          <div className="text-xs text-gray-500">
                            {model.minPlanRequired !== 'FREE' && (
                              <span className="inline-block bg-yellow-100 text-yellow-800 px-1 rounded text-xs mr-1">
                                {model.minPlanRequired}+
                              </span>
                            )}
                            Context: {model.contextWindow || 'Standard'}
                          </div>
                        </div>
                        <span className="text-xs text-blue-600 font-medium">
                          {model.creditMultiplier}x credits
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="text-xs text-gray-500 mt-1">Loading available models...</div>
      )}
    </div>
  );
};

export default ChapterLLMSelector;